import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WebDatatable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import ConfirmationDialog from 'app/shared/dialogs/confirmation';
import { Button } from 'app/shared/button';
import GmModal from 'app/shared/modal/modal';
import { PageContentWrapper } from 'app/layouts/wrapper/page-content';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Spacer } from 'app/layouts/generic';
import { truncateText } from 'app/shared/utils/general';
import { Badge } from 'app/shared/badge';
import { SegmentedTabs } from 'app/shared/segmented-tabs';
import { processDuplicateName } from 'app/shared/utils/mailing';
import { MobilePushMessageForm } from '../form';
import { useMobilePushMessageService } from 'hooks/messaging/mobile/message';
import {
  addOneMobilePushMessageToStore,
  loadMobilePushMessagesToStore
} from 'store/actions/push/mobile';
import { PermissionsContext } from 'contexts/permissions';

const message_type_tabs = [
  { value: 'campaign', label: 'Campaigns' },
  { value: 'transactional', label: 'Transactional' }
];

export const MobilePushListPage = () => {
  const history = useNavigate();
  const dispatch = useDispatch();
  const { permissions } = useContext(PermissionsContext);

  const { createMessage, fetchMessages, searchMessages, updateMessage } =
    useMobilePushMessageService();

  const { is_mobile_view } = useSelector((state) => state.metadata);
  const messages_in_store = useSelector((state) => state.mobi_push);

  const [messages, setMessages] = useState([]);
  const [is_search_mode, setSearchMode] = useState(false);
  const [loading, setLoading] = useState(false);

  const [show_cancel_confirmation, setShowCancelConfirmation] = useState(false);
  const [show_creation_modal, setShowCreationModal] = useState(false);
  const [message_to_edit, setMessageToEdit] = useState({});
  const [message_to_cancel, setMessageToCancel] = useState({});
  const [total_messages, setTotalMessages] = useState(0);
  const [message_type, setMessageType] = useState('campaign');

  useEffect(() => {
    const parsed_campaigns = Object.values(messages_in_store);
    setMessages(() => parsed_campaigns);
  }, [messages_in_store]);

  const config = {
    actions: {
      single: (message) => [
        [
          {
            value: 'view',
            label: 'View',
            hidden: !permissions['mobi_push:view']
          },
          {
            value: 'edit',
            label: 'Edit',
            hidden: !permissions['mobi_push:update']
          },
          { label: 'Clone', value: 'clone', hidden: !permissions['mobi_push:update'] }
        ],
        [
          {
            color: 'red',
            label: 'Cancel',
            value: 'cancel',
            hidden: message.status !== 'queued' || !permissions['mobi_push:view']
          }
        ]
      ]
    },
    allow_bulk_action: true,
    css: {},
    fields: [
      {
        title: 'Title',
        key: 'title',
        isTitle: true,
        formatter: (value) => truncateText(value, 45) || ''
      },
      {
        title: 'Status',
        key: 'status',
        formatter: (value) => (
          <Badge
            type={value === 'queued' ? 'orange' : value === 'processed' ? 'green' : 'red'}
            text={value}
          />
        ),
        isBadge: true
      },
      {
        title: 'Type',
        key: 'type',
        formatter: (value) => (
          <Badge type={value === 'campaign' ? 'blue' : 'orange'} text={value} />
        ),
        isBadge: true
      },
      {
        title: 'Send date',
        key: 'schedule',
        formatter: (value) => {
          if (!value || !value?.date) return 'N/A';
          return new Date(value.date).toDateString();
        },
        isMetadata: true
      }
    ],
    is_search_mode,
    items: messages.sort((a, b) => b.time_stamp - a.time_stamp),
    num_of_rows: 10,
    search_key: 'title,message,sender_id',
    search_text: ''
  };

  const cancelQueued = async (item_data) => {
    if (!item_data.schedule || !item_data.schedule.date) return;

    const now = Date.now();
    const schedule_date = Date.parse(item_data.schedule.date);

    if (schedule_date < now || schedule_date - now < 5 * 60 * 1000) {
      toast.info('Cannot cancel message');
      return;
    }

    toast.info('Canceling Message...');
    const result = await updateMessage(item_data.id, { data: { status: 'draft' } });
    if (result) dispatch(addOneMobilePushMessageToStore({ ...item_data, status: 'draft' }));
  };

  const duplicate = async (message_data) => {
    const data = {
      audiences: message_data.audiences,
      message: message_data.message,
      schedule: message_data.schedule,
      sender_id: message_data.sender_id,
      sender_image: message_data.sender_image,
      status: 'draft',
      title: processDuplicateName(message_data.title, 'copy', '-'),
      type: message_data.type
    };

    toast.info('Cloning Message...');
    const { message } = await createMessage({ data });

    if (message) {
      dispatch(addOneMobilePushMessageToStore(message));
      toast.success('Cloning successful.');
    }
  };

  const handleCancelConfirmation = (permitted) => {
    if (permitted) {
      cancelQueued(message_to_cancel);
    }

    setMessageToCancel({});
    setShowCancelConfirmation(false);
  };

  const handleDatatableAction = (payload) => {
    const { name, type, data } = payload;

    if (type === 'single') {
      switch (name) {
        case 'edit':
          setMessageToEdit(data);
          setShowCreationModal(true);
          break;
        case 'clone':
          duplicate(data);
          break;
        case 'cancel':
          setMessageToCancel(data);
          setShowCancelConfirmation(true);
          break;
        default:
          history(`/mobile/${data.id}`);
      }
    }
  };

  const handleItemClick = (payload) => {
    const { id } = payload;
    history(`/mobile/${id}`);
  };

  const handleDataRequest = async (page, population = 10) => {
    try {
      setLoading(true);
      const { messages, size } = await fetchMessages({
        query_string: `type=${message_type}&page=${page}&population=${population}&sort_by=-created_on`
      });

      dispatch(loadMobilePushMessagesToStore(messages));
      setTotalMessages(size);
    } catch (e) {
      dispatch(loadMobilePushMessagesToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchRequest = async (keys, keyword, page, population = 10) => {
    if (!keys) return;
    try {
      setLoading(true);
      const { messages, size } = await searchMessages(keys, keyword, {
        query_string: `type=${message_type}&page=${page}&population=${population}&sort_by=-created_on`
      });

      setTotalMessages(size);
      dispatch(loadMobilePushMessagesToStore(messages));
    } catch (e) {
      dispatch(loadMobilePushMessagesToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleMessageTypeChange = (type) => {
    dispatch(loadMobilePushMessagesToStore([]));
    setMessageType(type);
  };

  const message_type_toggle = (
    <SegmentedTabs
      options={message_type_tabs}
      value={message_type}
      onChange={handleMessageTypeChange}
      label="Message type"
    />
  );

  const table_actions = (
    <>
      <Button
        icon_name="add"
        text="New Message"
        disabled={!permissions['mobi_push:create']}
        onClick={() => setShowCreationModal(true)}
      />
    </>
  );

  return (
    <PageContentWrapper>
      <GridRow>
        <GridColumn span={4}>
          {is_mobile_view ? (
            <>
              {message_type_toggle}
              <Spacer multiple={2} />
              <MobileDatatable
                key={message_type}
                config={config}
                action={handleDatatableAction}
                onClick={handleItemClick}
                onListModeChange={setSearchMode}
                onDataRequest={handleDataRequest}
                onSearchRequest={handleSearchRequest}
                showHeader
              />
            </>
          ) : (
            <WebDatatable
              header_tabs={message_type_toggle}
              reset_key={message_type}
              config={{
                ...config,
                total_count: total_messages
              }}
              action={handleDatatableAction}
              onClick={handleItemClick}
              checkbox
              loading_data={loading}
              table_actions={table_actions}
              onDataRequest={handleDataRequest}
              onSearchRequest={handleSearchRequest}
            />
          )}
          <ConfirmationDialog
            type="error"
            title="Cancel Message"
            message="Are you sure you want to cancel this message?"
            callback={handleCancelConfirmation}
            is_open={show_cancel_confirmation}
          />
          <GmModal
            position="right"
            show_modal={show_creation_modal}
            show_title
            title="Create message"
            onClose={() => setShowCreationModal(false)}
          >
            <MobilePushMessageForm message_data={message_to_edit} />
          </GmModal>
        </GridColumn>
      </GridRow>
    </PageContentWrapper>
  );
};
